import type { IssueDto } from '../dtos/IssueDto.js';
import { getSupabaseClient } from '../../../../core/supabase/client.js';

const SELECT_FIELDS = `
  id, org_id, project_id, location_id, session_id,
  name, description, status, priority, due_date,
  assigned_to_id, assigned_to_ids, completed_at, category_id,
  photos, created_at, updated_at, created_by,
  assigned_to:profiles!assigned_to_id(first_name, last_name),
  creator:profiles!created_by(first_name, last_name),
  category:metadatas!category_id(name),
  session:sessions!session_id(id, notes, scheduled_start_at),
  location:locations!issues_location_id_fkey(name)
`;

export class IssueRemoteDataSource {
  private get supabase() {
    return getSupabaseClient() as any;
  }

  async getIssueById(id: string): Promise<IssueDto | null> {
    const { data, error } = await this.supabase
      .from('issues')
      .select(SELECT_FIELDS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async listIssues(options: {
    orgId: string;
    projectId?: string;
    status?: string;
    priority?: string;
    assignedToId?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: IssueDto[]; total: number }> {
    let query = this.supabase
      .from('issues')
      .select(SELECT_FIELDS, { count: 'exact' })
      .eq('org_id', options.orgId)
      .order('created_at', { ascending: false });

    if (options.projectId) query = query.eq('project_id', options.projectId);
    if (options.status) query = query.eq('status', options.status);
    if (options.priority) query = query.eq('priority', options.priority);
    if (options.assignedToId) {
      query = query.or(`assigned_to_id.eq.${options.assignedToId},assigned_to_ids.cs.{${options.assignedToId}}`);
    }
    if (options.search) {
      const s = options.search.replace(/[,()]/g, ' ').trim();
      if (s) query = query.or(`name.ilike.%${s}%,description.ilike.%${s}%`);
    }
    if (typeof options.limit === 'number' && typeof options.offset === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options.limit === 'number') {
      query = query.limit(options.limit);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  async countIssues(options: {
    orgId: string;
    projectId?: string;
  }): Promise<{ open: number; inProgress: number; closed: number; overdue: number }> {
    const base = () => {
      let q = this.supabase
        .from('issues')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', options.orgId);
      if (options.projectId) q = q.eq('project_id', options.projectId);
      return q;
    };
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' });
    const [open, inProgress, closed, overdue] = await Promise.all([
      base().eq('status', 'open'),
      base().eq('status', 'inProgress'),
      base().eq('status', 'closed'),
      base().neq('status', 'closed').lt('due_date', today),
    ]);
    for (const r of [open, inProgress, closed, overdue]) {
      if (r.error) throw new Error(r.error.message);
    }
    return {
      open: open.count ?? 0,
      inProgress: inProgress.count ?? 0,
      closed: closed.count ?? 0,
      overdue: overdue.count ?? 0,
    };
  }

  async createIssue(payload: Partial<IssueDto> & { org_id: string; project_id: string; name: string; created_by: string }): Promise<IssueDto | null> {
    const { data, error } = await this.supabase
      .from('issues')
      .insert(payload)
      .select(SELECT_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async updateIssue(id: string, payload: Partial<IssueDto>): Promise<IssueDto | null> {
    const { data, error } = await this.supabase
      .from('issues')
      .update(payload)
      .eq('id', id)
      .select(SELECT_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async deleteIssue(id: string): Promise<void> {
    const { error } = await this.supabase.from('issues').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }
}
